import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Music4, Film, Download, ArrowLeft, Sparkles, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { mistApi, pipelinesApi, streamAsset } from "@/api/mist";

function Thumb({ id, active }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let live = true; streamAsset(id).then((u) => live && setUrl(u)).catch(() => {}); return () => { live = false; }; }, [id]);
  return (
    <div className={`relative aspect-square border-2 overflow-hidden ${active ? "border-[#FFE08A]" : "border-black"}`}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#1a1a1a] grid place-items-center"><Loader2 size={14} className="animate-spin text-white/40" /></div>}
      {active && <div className="absolute top-1 right-1 w-5 h-5 grid place-items-center bg-[#FFE08A] border-2 border-black"><Check size={11} className="text-black" /></div>}
    </div>
  );
}

export default function MusicVideoStudio() {
  const nav = useNavigate();
  const [lib, setLib] = useState(null);
  const [songId, setSongId] = useState("");
  const [picked, setPicked] = useState([]);
  const [hook, setHook] = useState("off");
  const [textMode, setTextMode] = useState("off");
  const [caption, setCaption] = useState("");
  const [phase, setPhase] = useState("setup");
  const [progress, setProgress] = useState(0);
  const [resultUrl, setResultUrl] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    mistApi.library().then((d) => {
      setLib(d);
      if ((d.audio || [])[0]) setSongId(d.audio[0].id);
    }).catch(() => setLib({ images: [], audio: [], videos: [] }));
  }, []);

  const toggleImg = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : (p.length >= 8 ? p : [...p, id]));

  const render = async () => {
    if (!songId) return toast.error("Pick a song first");
    if (hook !== "ai" && picked.length === 0) return toast.error("Pick at least one image (or turn the AI hook on)");
    setPhase("rendering"); setProgress(6); setErr("");
    try {
      const start = await pipelinesApi.musicVideo60({
        song_asset_id: songId, still_asset_ids: picked, hook,
        text_mode: textMode, caption: caption.trim(),
      });
      const done = await pipelinesApi.poll(start.job_id, {
        interval: 6000, timeout: 600000,
        onTick: (j) => { if (typeof j.progress === "number") setProgress(Math.max(6, j.progress)); },
      });
      if (done.status === "completed" && done.asset_id) {
        const u = await streamAsset(done.asset_id);
        setResultUrl(u); setResultId(done.asset_id); setProgress(100); setPhase("done");
      } else {
        setErr(done.error || "Render failed"); setPhase("error");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Render failed"); setPhase("error");
    }
  };

  const images = (lib?.images) || [];
  const audio = (lib?.audio) || [];
  const costCredits = hook === "ai" ? 24 : 1;

  return (
    <div className="absolute inset-0 bg-[#0A0A0A] text-white overflow-y-auto no-scrollbar" data-testid="music-video-studio">
      <div className="px-4 pt-5 pb-3 border-b-2 border-[#1a1a1a] flex items-center gap-2">
        <button onClick={() => nav(-1)} data-testid="mv-back" className="w-8 h-8 grid place-items-center border-2 border-white/20"><ArrowLeft size={15} /></button>
        <div>
          <p className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-[0.24em] text-[#FFE08A]"><Film size={12} /> Music Video · 60s</p>
          <h1 className="font-serif text-2xl leading-none">Make a music video</h1>
        </div>
      </div>

      {lib == null ? (
        <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-[#FFE08A]" /></div>
      ) : phase === "done" ? (
        <div className="p-4 space-y-3" data-testid="mv-result">
          <video src={resultUrl} controls playsInline className="w-full border-2 border-black bg-black" style={{ aspectRatio: "9/16" }} />
          <a href={resultUrl} download="misteck-music-video.mp4" data-testid="mv-download" className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-serif text-lg py-3 shadow-[4px_4px_0px_#000]"><Download size={16} /> Download MP4</a>
          <button onClick={() => { setPhase("setup"); setResultUrl(null); setProgress(0); }} data-testid="mv-again" className="w-full border-2 border-white/25 py-2.5 font-mono text-[10px] font-black uppercase tracking-widest">Make another</button>
        </div>
      ) : phase === "rendering" ? (
        <div className="p-6 text-center space-y-4" data-testid="mv-rendering">
          <Loader2 size={26} className="animate-spin text-[#FFE08A] mx-auto" />
          <p className="font-serif text-xl">Building your video…</p>
          <div className="h-2 bg-[#1a1a1a] border-2 border-black overflow-hidden"><div className="h-full bg-[#FFE08A] transition-all" style={{ width: `${progress}%` }} /></div>
          <p className="font-mono text-[10px] text-white/50">Stills + song render fast. An AI hook adds a couple of minutes.</p>
        </div>
      ) : (
        <div className="p-4 space-y-5">
          {phase === "error" && <p className="font-mono text-[11px] text-red-400" data-testid="mv-error">{err}</p>}

          <div>
            <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A] mb-2">1 · Pick a song</p>
            {audio.length === 0 ? <p className="font-mono text-[10px] text-white/40">No songs in your Vault yet — make one first.</p> : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto no-scrollbar" data-testid="mv-song-list">
                {audio.map((a) => (
                  <button key={a.id} onClick={() => setSongId(a.id)} data-testid={`mv-song-${a.id}`}
                    className={`w-full flex items-center gap-2 border-2 px-3 py-2 text-left ${songId === a.id ? "border-[#FFE08A] bg-[#1a1630]" : "border-black bg-[#141414]"}`}>
                    <Music4 size={13} className="text-[#C9B6FF] shrink-0" />
                    <span className="font-mono text-[11px] truncate">{a.title || a.filename || "Untitled"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A] mb-2">2 · Pick images <span className="text-white/40">({picked.length}/8)</span></p>
            {images.length === 0 ? <p className="font-mono text-[10px] text-white/40">No images in your Vault yet.</p> : (
              <div className="grid grid-cols-4 gap-1.5" data-testid="mv-image-grid">
                {images.slice(0, 24).map((im) => (
                  <button key={im.id} onClick={() => toggleImg(im.id)} data-testid={`mv-img-${im.id}`}><Thumb id={im.id} active={picked.includes(im.id)} /></button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A] mb-2">3 · Hook</p>
              <div className="flex gap-1.5">
                {["off", "ai"].map((h) => (
                  <button key={h} onClick={() => setHook(h)} data-testid={`mv-hook-${h}`}
                    className={`flex-1 border-2 py-2 font-mono text-[9px] font-black uppercase ${hook === h ? "border-[#FFE08A] bg-[#FFE08A] text-black" : "border-black bg-[#141414] text-white/60"}`}>{h === "off" ? "Off" : "AI 8s"}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A] mb-2">4 · Lyrics text</p>
              <select value={textMode} onChange={(e) => setTextMode(e.target.value)} data-testid="mv-text-mode"
                className="w-full bg-[#141414] border-2 border-black text-white font-mono text-[10px] py-2 px-2">
                <option value="off">Off</option>
                <option value="karaoke">Karaoke (lower)</option>
                <option value="word">Centered</option>
                <option value="hollywood">Scroll up</option>
              </select>
            </div>
          </div>

          {textMode !== "off" && (
            <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="mv-caption"
              placeholder="Text to show (e.g. a hook line or title)"
              className="w-full bg-[#0e0e0e] border-2 border-black text-white font-mono text-[11px] px-3 py-2.5 focus:border-[#FFE08A] outline-none placeholder:text-white/25" />
          )}

          <button onClick={render} data-testid="mv-render-btn"
            className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-serif text-lg py-3 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            {hook === "ai" ? <Wand2 size={16} /> : <Sparkles size={16} />} Render 60s video · {costCredits} {costCredits === 1 ? "credit" : "credits"}
          </button>
        </div>
      )}
    </div>
  );
}
